import { z } from 'zod';
import { keyBundleSchema } from './key.schema.js';
import { passwordField } from './auth.schema.js';
/**
 * Fields accepted by PATCH /users/me. Every field is optional so the
 * caller can update one at a time. Practitioner fields are handled by
 * `updatePractitionerProfileSchema` in auth.schema.ts (which enforces
 * the 3-letter-prefix + 10-digit AHPRA format) — this schema covers
 * the everyday profile-edit fields.
 */
export const updateCurrentUserSchema = z
    .object({
    displayName: z
        .string()
        .trim()
        .min(2, 'displayName must be at least 2 characters')
        .max(64, 'displayName cannot exceed 64 characters')
        .optional(),
    /**
     * Empty string is allowed as a "clear avatar" signal; otherwise the
     * value must be an http(s) URL up to 2048 characters. Data:,
     * javascript:, and other URI schemes are rejected.
     */
    avatarUrl: z
        .string()
        .max(2048, 'avatarUrl cannot exceed 2048 characters')
        .refine((v) => v === '' || /^https?:\/\//i.test(v), {
        message: 'avatarUrl must be an http(s) URL',
    })
        .optional(),
    // Practitioner fields pass through untouched here; the route
    // re-parses them with updatePractitionerProfileSchema.
    isPractitioner: z.boolean().optional(),
    ahpraNumber: z.string().min(0).max(32).optional().nullable(),
    /**
     * Server-side default workspace (the web app's star). Null clears the
     * stored value. Membership is re-validated server-side on write —
     * this only guarantees shape. Must live in this .strict() object or
     * the PATCH 400s on an unknown key.
     */
    defaultOrgId: z
        .string()
        .uuid('defaultOrgId must be a valid organisation id')
        .optional()
        .nullable(),
})
    .strict();
export const pushTokenSchema = z.object({
    token: z
        .string()
        .trim()
        .min(1, 'push token is required')
        .max(512, 'push token cannot exceed 512 characters'),
});
/**
 * POST /users/me/password. The password is also the E2EE key-encryption
 * secret (the private key is wrapped under a key derived from it), so a
 * password change must re-wrap the bundle in the SAME request — otherwise a
 * failure between the two writes leaves the account unable to unlock its
 * keys. `keyBundle` is optional only for legacy clients that have not yet
 * been updated to re-wrap; the server logs a warning when it is omitted and
 * never wipes the existing bundle.
 */
export const changePasswordSchema = z
    .object({
    currentPassword: z
        .string()
        .min(1, 'currentPassword is required')
        .max(128, 'currentPassword cannot exceed 128 characters'),
    // Same policy as register / join / reset (upper, lower, digit, symbol).
    newPassword: passwordField,
    keyBundle: keyBundleSchema.optional(),
})
    .strict();
//# sourceMappingURL=user.schema.js.map